import * as L from 'leaflet'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'

interface ArcOptions extends L.PathOptions {
  radius: number
  startAngle: number
  endAngle: number
  clockwise: boolean
}

class Arc extends L.Path {
  override options: ArcOptions
  latlng: L.LatLng
  _mRadius: number
  override _map: MyMap

  constructor(latLng: L.LatLngExpression, map: MyMap, options?: ArcOptions) {
    super(options)
    this.options = {
      radius: 0,
      startAngle: 0,
      endAngle: 270,
      clockwise: true,
      ...options,
    }

    L.Util.setOptions(this, options)
    this.latlng = L.latLng(latLng)
    this._mRadius = this.options.radius
    this._map = map
    this._project()
  }

  getBounds(): L.LatLngBounds {
    const _ = this as any
    const xy = [_._radius, _._radiusY || _._radius]
    return new L.LatLngBounds(
      this._map.layerPointToLatLng(_._point.subtract(xy)),
      this._map.layerPointToLatLng(_._point.add(xy))
    )
  }

  private _project() {
    const lng = this.latlng.lng
    const lat = this.latlng.lat
    const map = this._map
    const Earth = L.CRS.Earth
    const crs = map.options.crs

    const degToRad = Math.PI / 180
    const mRadiusInRadians = this._mRadius / (Earth as any).R / degToRad

    let projection1, projection2, center, distanceRadian
    if (crs && crs.distance === Earth.distance) {
      projection1 = map.project([lat + mRadiusInRadians, lng])
      projection2 = map.project([lat - mRadiusInRadians, lng])
      center = projection1.add(projection2).divideBy(2)
      const unprojectedCenterLat = map.unproject(center).lat
      distanceRadian =
        Math.acos(
          (Math.cos(mRadiusInRadians * degToRad) -
            Math.sin(lat * degToRad) *
              Math.sin(unprojectedCenterLat * degToRad)) /
            (Math.cos(lat * degToRad) *
              Math.cos(unprojectedCenterLat * degToRad))
        ) / degToRad

      if (Number.isNaN(distanceRadian) || distanceRadian === 0) {
        distanceRadian = mRadiusInRadians / Math.cos((Math.PI / 180) * lat)
      }
      const _ = this as any
      _._point = center.subtract(map.getPixelOrigin())
      _._radius = Number.isNaN(distanceRadian)
        ? 0
        : center.x - map.project([unprojectedCenterLat, lng - distanceRadian]).x
      _._radiusY = center.y - projection1.y
    } else {
      const _ = this as any
      if (crs) {
        const shiftedLatLng = crs.unproject(
          crs.project(this.latlng).subtract([this._mRadius, 0])
        )
        _._point = map.latLngToLayerPoint(this.latlng)
        _._radius = _._point.x - map.latLngToLayerPoint(shiftedLatLng).x
      }
    }
  }

  public path(): {
    start: { x: number; y: number }
    end: { x: number; y: number }
    path: string[]
    bounds: L.LatLngBounds
  } {
    const _ = this as any
    const options = this.options
    const point = _._point
    const radius = _._radius
    const rotatedStart = this._rotatePoint(
      point.x,
      point.y,
      radius,
      options.startAngle,
      options.clockwise
    )
    const pathData = [
      `M${rotatedStart.x.toFixed(2)}`,
      rotatedStart.y.toFixed(2),
      `A${radius}`,
      radius,
      0,
      options.endAngle - options.startAngle >= 180 ? 1 : 0,
      options.clockwise ? 0 : 1,
    ]
    const rotatedEnd = this._rotatePoint(
      point.x,
      point.y,
      radius,
      options.endAngle,
      options.clockwise
    )
    pathData.push(rotatedEnd.x.toFixed(2), rotatedEnd.y.toFixed(2))

    return {
      start: rotatedStart,
      end: rotatedEnd,
      path: pathData,
      bounds: this.getBounds(),
    }
  }

  private _rotatePoint(
    x: number,
    y: number,
    radius: number,
    angle: number,
    clockwise: boolean
  ): { x: number; y: number } {
    const angleInRadians = (angle / 180) * Math.PI
    const cosTheta = radius * Math.cos(angleInRadians)
    const sinTheta = radius * Math.sin(angleInRadians)
    return clockwise
      ? { x: x - sinTheta, y: y + cosTheta }
      : { x: x + cosTheta, y: y + sinTheta }
  }
}

const arc = (latLng: L.LatLngExpression, map: MyMap, options?: ArcOptions) => {
  return new Arc(latLng, map, options).path()
}

export { Arc, arc }
